import { FC, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { useRbacHook } from "../../../hooks/useRbacHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { UserCategory, CATEGORY_DISPLAY_LABELS } from "../../../apis/rbac/rbac.constants";

const roleSchema = z.object({
  name: z
    .string()
    .min(1, "Role name is required")
    .min(2, "Role name must be at least 2 characters")
    .max(200, "Role name must not exceed 200 characters"),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  userCategory: z
    .nativeEnum(UserCategory)
    .optional()
    .or(z.literal("" as any).transform(() => undefined)),
});

type RoleFormData = z.infer<typeof roleSchema>;

interface AddRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddRoleDialog: FC<AddRoleDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const { createRole, isCreatingRole } = useRbacHook();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: "",
      description: "",
      userCategory: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({ name: "", description: "", userCategory: undefined });
    }
  }, [open, reset]);

  const onSubmit = async (data: RoleFormData) => {
    if (!selectedInstitute?.id) {
      logger.show_error(
        "Please select an institute from the dashboard before creating a role.",
      );
      return;
    }

    try {
      await createRole({
        name: data.name,
        description: data.description || undefined,
        userCategory: data.userCategory || undefined,
      });
      logger.show_success("Role created successfully!");
      onClose();
      onSuccess?.();
    } catch {
      logger.show_error("Failed to create role. Please try again.");
    }
  };

  const handleClose = () => {
    if (!isCreatingRole) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="add-role-dialog"
    >
      <DialogTitle>Add Role</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="name"
                id="add-role-name"
                label="Role Name"
                isGridItem={false}
                fullWidth
                required
                inputProps={{ "data-testid": "add-role-name" }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="description"
                id="add-role-description"
                label="Description"
                isGridItem={false}
                fullWidth
                multiline
                rows={3}
                inputProps={{ "data-testid": "add-role-description" }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="userCategory"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel id="user-category-label">User Category (optional)</InputLabel>
                    <Select
                      {...field}
                      labelId="user-category-label"
                      label="User Category (optional)"
                      value={field.value ?? ""}
                      inputProps={{ "data-testid": "add-role-user-category" }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {Object.values(UserCategory).map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {CATEGORY_DISPLAY_LABELS[cat]}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isCreatingRole}
            data-testid="add-role-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={isCreatingRole ? undefined : <Save />}
            disabled={isCreatingRole}
            data-testid="add-role-save"
          >
            {isCreatingRole ? "Creating..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddRoleDialog;

